import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import API_URL from "../../config";

const UsersList = () => {
  const [allUsers, setAllUsers] = useState([]);
  const [activeFilter, setActiveFilter] = useState("ALL");
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [lastLoginFilter, setLastLoginFilter] = useState("all");

  const usersPerPage = 9;
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (location.state?.label) {
      setActiveFilter(location.state.label);
      setCurrentPage(1);
    } else {
      setActiveFilter("ALL");
    }
  }, [location.state]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    const res = await axios.post(`${API_URL}/api/auth/getUsers`);
    setAllUsers(
      res.data.data.map((u) => ({
        ...u,
        createdAt: new Date(u.createdAt).toLocaleDateString(),
      })),
    );
  };

  const applyLabelFilter = (users) => {
    switch (activeFilter) {
      case "EXPIRED":
        return users.filter((u) => u.subscriptionStatus === "expired");
      case "FREE":
        return users.filter((u) => u.subscriptionPlan === "FREE TRIAL");
      case "CUSTOM":
        return users.filter((u) => u.subscriptionPlan === "Custom Flex Plan");
      case "SPECIAL":
        return users.filter((u) => u.subscriptionPlan === "Special Flex Plan");
      case "LEAN":
        return users.filter((u) => u.subscriptionPlan === "LEAN PLAN");
      case "LEAP":
        return users.filter((u) => u.subscriptionPlan === "LEAP PLAN");
      default:
        return users;
    }
  };

  const applyLastLoginFilter = (users) => {
    if (lastLoginFilter === "all") return users;

    const now = new Date();
    const daysMap = { "7days": 7, "15days": 15, "30days": 30 };
    const days = daysMap[lastLoginFilter];

    return users.filter((u) => {
      const loginDate = u.lastLogin
        ? new Date(u.lastLogin)
        : u.updatedAt
          ? new Date(u.updatedAt)
          : null;

      if (!loginDate) return false;

      const diffDays = (now - loginDate) / (1000 * 60 * 60 * 24);
      return diffDays <= days;
    });
  };

  const filteredUsers = useMemo(() => {
    let users = applyLabelFilter(allUsers);
    users = applyLastLoginFilter(users);

    return users.filter(
      (u) =>
        u.firstname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.lastname.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [allUsers, searchTerm, activeFilter, lastLoginFilter]);

  const indexOfLast = currentPage * usersPerPage;
  const currentUsers = filteredUsers.slice(
    indexOfLast - usersPerPage,
    indexOfLast,
  );
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const getPaginationRange = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];
    let lastPage = null;

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= currentPage - delta && i <= currentPage + delta)
      ) {
        range.push(i);
      }
    }

    for (let i of range) {
      if (lastPage) {
        if (i - lastPage === 2) rangeWithDots.push(lastPage + 1);
        else if (i - lastPage > 2) rangeWithDots.push("...");
      }
      rangeWithDots.push(i);
      lastPage = i;
    }

    return rangeWithDots;
  };

  return (
    <div className="body-wrapper py-0">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex gap-3">
          <h4 className="fw-bold" style={{ color: "#6600cc" }}>
            Users
          </h4>
          <select
            onChange={(e) => {
              setLastLoginFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="btn text-black border-1"
          >
            <option value="" hidden>
              Filter by last login
            </option>
            <option value="all">All</option>
            <option value="7days">7 Days</option>
            <option value="15days">15 Days</option>
            <option value="30days">30 Days</option>
          </select>
        </div>

        <input
          type="text"
          className="form-control w-25 fw-5 bg-transparent text-black search-input"
          placeholder="Search users..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setCurrentPage(1);
          }}
        />
      </div>

      <div className="row">
        {currentUsers.map((user) => (
          <div className="col-xl-4 col-lg-6 mb-4" key={user._id}>
            <div className="card h-100 shadow-sm">
              <div className="card-body">
                <div className="d-flex align-items-center mb-3">
                  {user.headshot ? (
                    <>
                      <img
                        src={
                          user.headshot
                            ? `https://0.0.0.0:5000${user.headshot}`
                            : "/assets/avatar.png"
                        }
                        className="rounded-circle"
                        width="56"
                        height="56"
                        alt="avatar"
                      />
                    </>
                  ) : (
                    <>
                      <div
                        style={{
                          color: "#000",
                          backgroundColor: "rgba(102,0,204, 0.1)",
                        }}
                        className="rounded-circle p-2"
                      >
                        {user?.firstname.charAt(0).toUpperCase()}
                        {user?.lastname.charAt(0).toUpperCase()}
                      </div>
                    </>
                  )}
                  <div className="ms-3">
                    <h6 className="fw-semibold text-black mb-0 text-capitalize">
                      {user.firstname} {user.lastname}
                    </h6>
                    <small className="text-black text-break">
                      {user.email}
                    </small>
                  </div>
                </div>

                <div className="mb-3 text-black small">
                  <div>Plan: {user.subscriptionPlan}</div>
                  <div className="text-capitalize">
                    Status: {user.subscriptionStatus}
                  </div>
                  <div>Joined: {user.createdAt}</div>
                  <div>
                    Last Login:{" "}
                    {user.lastLogin ? (
                      <>
                        {new Date(user.lastLogin).toLocaleDateString("en-IN")}
                      </>
                    ) : (
                      <>N/A</>
                    )}
                  </div>
                </div>

                <div className="d-flex gap-2">
                  <button
                    className="btn btn-outline-success btn-sm"
                    onClick={() =>
                      navigate("/career-path", {
                        state: {
                          user: user._id,
                          userName: `${user.firstname} ${user.lastname}`,
                        },
                      })
                    }
                  >
                    Roadmap
                  </button>
                  <button
                    className="btn btn-primary btn-sm text-capitalize"
                    onClick={() => {
                      navigate("/user-activity", {
                        state: {
                          userData: user,
                        },
                      });
                    }}
                  >
                    view activity
                  </button>
                  <button
                    className="btn btn-outline-success rounded-3 btn-sm text-capitalize"
                    onClick={() => {
                      navigate(`/admin/token-usage/${user._id}`);
                    }}
                  >
                    view token usage
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <ul className="pagination mb-0">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{ backgroundColor: "#6600cc", color: "#fff" }}
              >
                Prev
              </button>
            </li>

            {getPaginationRange().map((page, idx) =>
              page === "..." ? (
                <li key={idx} className="page-item disabled">
                  <span className="page-link bg-transparent text-black">…</span>
                </li>
              ) : (
                <li
                  key={idx}
                  className={`page-item ${
                    currentPage === page ? "active bg-success text-dark" : ""
                  }`}
                >
                  <button
                    className="page-link bg-transparent text-black"
                    onClick={() => setCurrentPage(page)}
                    style={{ border: "solid 1px #6600cc", color: "#fff" }}
                  >
                    {page}
                  </button>
                </li>
              ),
            )}

            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <button
                className="page-link"
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                style={{ backgroundColor: "#6600cc", color: "#fff" }}
              >
                Next
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default UsersList;
